// The getUniqueId scheme is reproduced from dijit/registry —
// Copyright (c) The JS Foundation, BSD-3-Clause. See ../../LICENSE.
// mds-dojo — dijit/registry. Global widget registry keyed by id.
// getUniqueId reproduces dojo's scheme EXACTLY (widgetType + "_" + counter,
// counter per type starting at 0), because the oracle asserts on ids like
// "dojox_widget_Standby_0" and selects nodes by the [widgetid] attribute.

const hash: Record<string, any> = {};
const widgetTypeCtr: Record<string, number> = {};

export function add(widget: any): void {
  if (hash[widget.id]) {
    throw new Error('Tried to register widget with id==' + widget.id + ' but that id is already registered');
  }
  hash[widget.id] = widget;
  registry.length++;
}

export function remove(id: string): void {
  if (hash[id]) {
    delete hash[id];
    registry.length--;
  }
}

export function byId(id: string | any): any {
  return typeof id === 'string' ? hash[id] : id;
}

export function byNode(node: Element): any {
  return hash[node.getAttribute('widgetId') as string];
}

export function getUniqueId(widgetType: string): string {
  let id: string;
  do {
    id = widgetType + '_' + (widgetType in widgetTypeCtr ? ++widgetTypeCtr[widgetType] : (widgetTypeCtr[widgetType] = 0));
  } while (hash[id]);
  return id; // dijit._scopeName === "dijit" → no scope prefix
}

export function findWidgets(root: Node, skipNode?: Node): any[] {
  const out: any[] = [];
  function helper(r: Node) {
    for (let node = r.firstChild; node; node = node.nextSibling) {
      if (node.nodeType === 1) {
        const el = node as Element;
        const widgetId = el.getAttribute('widgetId');
        if (widgetId) {
          const w = hash[widgetId];
          if (w) { out.push(w); }
        } else if (node !== skipNode) {
          helper(node);
        }
      }
    }
  }
  helper(root);
  return out;
}

export function getEnclosingWidget(node: Node | null): any {
  while (node) {
    const id = (node as Element).nodeType === 1 && (node as Element).getAttribute('widgetId');
    if (id) { return hash[id]; }
    node = node.parentNode;
  }
  return null;
}

export function toArray(): any[] {
  return Object.keys(hash).map((k) => hash[k]);
}

const registry = {
  length: 0,
  add, remove, byId, byNode, getUniqueId, findWidgets, getEnclosingWidget, toArray,
  _hash: hash,
};
// Real dojo published `dijit` (with .registry) on window before any module ran.
// Widget code (and prototype-hoisted field initializers that construct widgets at
// MODULE-EVAL time) reads window.dijit.registry — publish here, at the earliest
// point of the dependency graph, instead of relying on entry-file import order.
if (typeof window !== 'undefined') {
  (window as any).dijit = Object.assign((window as any).dijit || {}, { registry });
}

export default registry;
